import { call, delay, put, race, select, take, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  ApiKey,
  JobStatus,
  PaginatedResponse,
  Project,
  Translation,
  TranslationJob,
  TranslationKey,
} from '../../../helpers/types';
import { projectsApi, AddMemberPayload } from '../../../services/projectsApi';
import { ImportKeysResult, keysApi } from '../../../services/keysApi';
import { translationsApi } from '../../../services/translationsApi';
import { jobsApi } from '../../../services/jobsApi';
import { apiKeysApi, CreateApiKeyPayload } from '../../../services/apiKeysApi';
import { notificationsActions } from '../../Notifications/slice';
import { workspaceActions as actions } from '.';
import { selectKeysSearch, selectSelectedLanguage } from './selectors';
import {
  AiTranslateKeyPayload,
  CreateKeyPayload,
  FetchKeysPayload,
  ImportKeysPayload,
  ReviewPayload,
  StartTranslateJobPayload,
  UpsertTranslationPayload,
} from './types';

function notifyError(error: unknown, fallback: string) {
  const message = error instanceof Error ? error.message : fallback;
  return notificationsActions.setNotification({ message, type: 'error' });
}

function* refreshKeyTranslation(
  projectId: string,
  keyId: string,
  language: string,
) {
  const translations = (yield call(
    translationsApi.listForKey,
    projectId,
    keyId,
  )) as Translation[];
  const match = translations.find((t) => t.language === language);
  if (match) {
    yield put(actions.translationUpdated({ keyId, translation: match }));
  } else {
    yield put(actions.translationOpFailed());
  }
}

function* handleLoadWorkspace(action: PayloadAction<string>) {
  const projectId = action.payload;
  try {
    const project = (yield call(projectsApi.get, projectId)) as Project;
    yield put(actions.projectLoaded(project));
    yield put(actions.fetchActiveJobRequest(projectId));
    yield put(
      actions.fetchKeysRequest({ projectId, page: 1, search: '' }),
    );
    if (project.targetLanguages.length) {
      yield put(
        actions.fetchTranslationsRequest({
          projectId,
          language: project.targetLanguages[0],
        }),
      );
    }
  } catch (error) {
    yield put(actions.projectLoadFailed());
    yield put(notifyError(error, 'Failed to load project'));
  }
}

function* handleFetchKeys(action: PayloadAction<FetchKeysPayload>) {
  try {
    const { projectId, page, search } = action.payload;
    const data = (yield call(keysApi.list, projectId, {
      page,
      limit: 25,
      search,
    })) as PaginatedResponse<TranslationKey>;
    yield put(actions.keysLoaded(data));
  } catch (error) {
    yield put(actions.keysFailed());
    yield put(notifyError(error, 'Failed to load keys'));
  }
}

function* handleFetchTranslations(
  action: PayloadAction<{ projectId: string; language: string }>,
) {
  try {
    const { projectId, language } = action.payload;
    const data = (yield call(translationsApi.list, projectId, {
      language,
      limit: 200,
    })) as PaginatedResponse<Translation>;
    yield put(actions.translationsLoaded(data.data));
  } catch (error) {
    yield put(actions.translationsFailed());
    yield put(notifyError(error, 'Failed to load translations'));
  }
}

function* handleCreateKey(action: PayloadAction<CreateKeyPayload>) {
  try {
    const { projectId, ...payload } = action.payload;
    const key = (yield call(
      keysApi.create,
      projectId,
      payload,
    )) as TranslationKey;
    yield put(actions.keyCreated(key));
    yield put(
      notificationsActions.setNotification({
        message: 'Key created',
        type: 'success',
      }),
    );
  } catch (error) {
    yield put(notifyError(error, 'Failed to create key'));
  }
}

function* handleDeleteKey(
  action: PayloadAction<{ projectId: string; keyId: string }>,
) {
  try {
    yield call(keysApi.remove, action.payload.projectId, action.payload.keyId);
    yield put(actions.keyDeleted(action.payload.keyId));
  } catch (error) {
    yield put(notifyError(error, 'Failed to delete key'));
  }
}

function* handleUpsert(action: PayloadAction<UpsertTranslationPayload>) {
  try {
    const { projectId, keyId, language, value } = action.payload;
    const translation = (yield call(
      translationsApi.upsert,
      projectId,
      keyId,
      language,
      value,
    )) as Translation;
    yield put(actions.translationUpdated({ keyId, translation }));
  } catch (error) {
    yield put(actions.translationOpFailed());
    yield put(notifyError(error, 'Failed to save translation'));
  }
}

function* handleAiTranslateKey(action: PayloadAction<AiTranslateKeyPayload>) {
  try {
    const { projectId, keyId, language } = action.payload;
    yield call(translationsApi.translateKey, projectId, keyId, {
      languages: [language],
      overwriteApproved: false,
    });
    yield call(refreshKeyTranslation, projectId, keyId, language);
  } catch (error) {
    yield put(actions.translationOpFailed());
    yield put(notifyError(error, 'AI translation failed'));
  }
}

function* handleReview(action: PayloadAction<ReviewPayload>) {
  try {
    const { projectId, translationId, decision, note } = action.payload;
    const translation = (yield call(translationsApi.review, projectId, translationId, {
      decision,
      note,
    })) as Translation;
    yield put(
      actions.translationUpdated({
        keyId: action.payload.keyId,
        translation,
      }),
    );
  } catch (error) {
    yield put(actions.translationOpFailed());
    yield put(notifyError(error, 'Review failed'));
  }
}

function* handleImportKeys(action: PayloadAction<ImportKeysPayload>) {
  try {
    const { projectId, keys, prune } = action.payload;
    const result = (yield call(
      keysApi.import,
      projectId,
      keys,
      prune,
    )) as ImportKeysResult;
    const search = (yield select(selectKeysSearch)) as string;
    yield put(actions.fetchKeysRequest({ projectId, page: 1, search }));
    const language = (yield select(selectSelectedLanguage)) as string | null;
    if (language) {
      yield put(actions.fetchTranslationsRequest({ projectId, language }));
    }
    yield put(
      notificationsActions.setNotification({
        message: `Imported ${result.created} new, updated ${result.updated}, skipped ${result.skipped}, removed ${result.deleted}`,
        type: 'success',
      }),
    );
  } catch (error) {
    yield put(notifyError(error, 'Import failed'));
  } finally {
    yield put(actions.importKeysDone());
  }
}

/**
 * Polls a background translation job until it finishes, mirroring progress into
 * the store so the UI can show a progress bar and keep the workspace locked.
 */
function* pollJob(projectId: string, jobId: string) {
  while (true) {
    let job: TranslationJob;
    try {
      job = (yield call(jobsApi.get, projectId, jobId)) as TranslationJob;
    } catch (error) {
      yield put(notifyError(error, 'Lost connection to the translation job'));
      yield put(actions.jobCleared());
      return;
    }

    yield put(actions.jobUpdated(job));

    if (job.status === JobStatus.COMPLETED || job.status === JobStatus.FAILED) {
      const language = (yield select(selectSelectedLanguage)) as string | null;
      const search = (yield select(selectKeysSearch)) as string;
      yield put(actions.fetchKeysRequest({ projectId, page: 1, search }));
      if (language) {
        yield put(actions.fetchTranslationsRequest({ projectId, language }));
      }

      if (job.status === JobStatus.COMPLETED) {
        yield put(
          notificationsActions.setNotification({
            message: `Translation finished: ${job.translated} translated, ${job.skipped} skipped${
              job.failed ? `, ${job.failed} failed` : ''
            }`,
            type: job.failed ? 'warning' : 'success',
          }),
        );
      } else {
        yield put(
          notificationsActions.setNotification({
            message: job.error || 'Translation job failed',
            type: 'error',
          }),
        );
      }
      return;
    }

    yield delay(2000);
  }
}

/** Polls a job but cancels cleanly when the workspace is torn down. */
function* watchJobLifecycle(projectId: string, jobId: string) {
  yield race({
    poll: call(pollJob, projectId, jobId),
    cancel: take(actions.resetWorkspace.type),
  });
}

function* handleStartTranslateJob(
  action: PayloadAction<StartTranslateJobPayload>,
) {
  try {
    const { projectId, languages } = action.payload;
    const job = (yield call(jobsApi.startTranslateMissing, projectId, {
      languages,
    })) as TranslationJob;
    yield put(actions.jobUpdated(job));
    yield call(watchJobLifecycle, projectId, job.id);
  } catch (error) {
    yield put(notifyError(error, 'Failed to start translation'));
  }
}

function* handleFetchActiveJob(action: PayloadAction<string>) {
  const projectId = action.payload;
  try {
    const job = (yield call(jobsApi.active, projectId)) as TranslationJob | null;
    if (
      job &&
      job.id &&
      (job.status === JobStatus.QUEUED || job.status === JobStatus.RUNNING)
    ) {
      yield put(actions.jobUpdated(job));
      yield call(watchJobLifecycle, projectId, job.id);
    } else {
      yield put(actions.jobUpdated(null));
    }
  } catch {
    yield put(actions.jobUpdated(null));
  }
}

function* handleAddLanguage(
  action: PayloadAction<{ projectId: string; code: string }>,
) {
  try {
    const project = (yield call(
      projectsApi.addLanguage,
      action.payload.projectId,
      action.payload.code,
    )) as Project;
    yield put(actions.projectUpdated(project));
  } catch (error) {
    yield put(notifyError(error, 'Failed to add language'));
  }
}

function* handleRemoveLanguage(
  action: PayloadAction<{ projectId: string; code: string }>,
) {
  try {
    const project = (yield call(
      projectsApi.removeLanguage,
      action.payload.projectId,
      action.payload.code,
    )) as Project;
    yield put(actions.projectUpdated(project));
  } catch (error) {
    yield put(notifyError(error, 'Failed to remove language'));
  }
}

function* handleAddMember(
  action: PayloadAction<{ projectId: string } & AddMemberPayload>,
) {
  try {
    const { projectId, ...payload } = action.payload;
    const project = (yield call(
      projectsApi.addMember,
      projectId,
      payload,
    )) as Project;
    yield put(actions.projectUpdated(project));
    yield put(
      notificationsActions.setNotification({
        message: 'Member added',
        type: 'success',
      }),
    );
  } catch (error) {
    yield put(notifyError(error, 'Failed to add member'));
  }
}

function* handleRemoveMember(
  action: PayloadAction<{ projectId: string; memberId: string }>,
) {
  try {
    const project = (yield call(
      projectsApi.removeMember,
      action.payload.projectId,
      action.payload.memberId,
    )) as Project;
    yield put(actions.projectUpdated(project));
  } catch (error) {
    yield put(notifyError(error, 'Failed to remove member'));
  }
}

function* handleFetchApiKeys(action: PayloadAction<string>) {
  try {
    const data = (yield call(apiKeysApi.list, action.payload)) as ApiKey[];
    yield put(actions.apiKeysLoaded(data));
  } catch (error) {
    yield put(notifyError(error, 'Failed to load API keys'));
  }
}

function* handleCreateApiKey(
  action: PayloadAction<{ projectId: string } & CreateApiKeyPayload>,
) {
  try {
    const { projectId, ...payload } = action.payload;
    const apiKey = (yield call(
      apiKeysApi.create,
      projectId,
      payload,
    )) as ApiKey;
    yield put(actions.apiKeyCreated(apiKey));
  } catch (error) {
    yield put(notifyError(error, 'Failed to create API key'));
  }
}

function* handleRevokeApiKey(
  action: PayloadAction<{ projectId: string; apiKeyId: string }>,
) {
  try {
    yield call(
      apiKeysApi.revoke,
      action.payload.projectId,
      action.payload.apiKeyId,
    );
    yield put(actions.fetchApiKeysRequest(action.payload.projectId));
  } catch (error) {
    yield put(notifyError(error, 'Failed to revoke API key'));
  }
}

export function* workspaceSaga() {
  yield takeLatest(actions.loadWorkspaceRequest.type, handleLoadWorkspace);
  yield takeLatest(actions.fetchKeysRequest.type, handleFetchKeys);
  yield takeLatest(actions.fetchTranslationsRequest.type, handleFetchTranslations);
  yield takeLatest(actions.createKeyRequest.type, handleCreateKey);
  yield takeLatest(actions.deleteKeyRequest.type, handleDeleteKey);
  yield takeLatest(actions.upsertTranslationRequest.type, handleUpsert);
  yield takeLatest(actions.aiTranslateKeyRequest.type, handleAiTranslateKey);
  yield takeLatest(actions.reviewRequest.type, handleReview);
  yield takeLatest(actions.importKeysRequest.type, handleImportKeys);
  yield takeLatest(
    actions.startTranslateJobRequest.type,
    handleStartTranslateJob,
  );
  yield takeLatest(actions.fetchActiveJobRequest.type, handleFetchActiveJob);
  yield takeLatest(actions.addLanguageRequest.type, handleAddLanguage);
  yield takeLatest(actions.removeLanguageRequest.type, handleRemoveLanguage);
  yield takeLatest(actions.addMemberRequest.type, handleAddMember);
  yield takeLatest(actions.removeMemberRequest.type, handleRemoveMember);
  yield takeLatest(actions.fetchApiKeysRequest.type, handleFetchApiKeys);
  yield takeLatest(actions.createApiKeyRequest.type, handleCreateApiKey);
  yield takeLatest(actions.revokeApiKeyRequest.type, handleRevokeApiKey);
}
